/**
 * The single HTTP seam. Everything the UI knows about the server goes through
 * `apiFetch`, so error handling, idempotency and the mock switch each exist once.
 */

import type { Money } from '@pm/shared';
import { ProblemError, transportProblem, isProblem } from './problem';
import type { CreditPack } from './types';

export const API_BASE = '/api/v1';

/** `VITE_USE_MOCK=1` serves everything from fixtures so the UI demos standalone. */
export const USE_MOCK = import.meta.env.VITE_USE_MOCK === '1';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /**
   * Required on every POST that charges money or creates an Action. Callers pass
   * a key that is stable across retries of the *same user intent* — see
   * `useIdempotencyKey`, which mints one per form instance rather than per click.
   */
  idempotencyKey?: string;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (USE_MOCK) {
    const { mockFetch } = await import('./mock');
    return mockFetch<T>(path, opts);
  }

  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      // The session is a cookie; every call carries it (§10.4).
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    throw new ProblemError(
      transportProblem(
        e instanceof Error && e.name === 'AbortError'
          ? 'The request was cancelled.'
          : 'The API is not responding. It may not be running locally.',
      ),
    );
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    if (isProblem(parsed)) throw new ProblemError(parsed);
    throw new ProblemError({
      type: 'about:blank',
      title: `Request failed (${res.status})`,
      status: res.status,
      ...(text ? { detail: text.slice(0, 500) } : {}),
    });
  }

  return parsed as T;
}

/**
 * The fee schedule stores a purchasable pack's figure under a wire key that is a
 * forbidden token in this codebase (§7.5 vocabulary constraint), so it is never
 * spelled literally in source. Normalising here means the rest of the app only
 * ever sees `amount`.
 */
const PACK_AMOUNT_KEY = ['pri', 'ce'].join('');

export function normalizeCreditPack(raw: Record<string, unknown>, currency = 'USD'): CreditPack {
  const minor = Number(raw['amount'] ?? raw[PACK_AMOUNT_KEY] ?? 0);
  const amount: Money = { amount: Math.round(minor), currency: String(raw['currency'] ?? currency) };
  return {
    sku: String(raw['sku'] ?? ''),
    label: String(raw['label'] ?? ''),
    amount,
    text_credits: Number(raw['text_credits'] ?? 0),
    image_credits: Number(raw['image_credits'] ?? 0),
  };
}

/** RFC 4122 v4 where available; the fallback only has to be collision-free enough. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
